/**
 * The moves a weekly cap held back in one run — a sheet of Compacting ▸ Why's Excel file AND a CSV of
 * their own (owner ruling 2026-10-30). Before it the Why file carried only two counts per transfer line
 * (`move_cap_deferred_moves` / `_units`, at the line's recipient and article); the held-back moves
 * themselves were listed only on a store's page, one store at a time (`./MoveCapDeferralsPanel.tsx`).
 *
 * **What is listed.** Every deferral the run recorded (bata-platform ADR 6100, `GET /inseason/runs/{run_id}/
 * move-cap-deferrals`) whose reason is one of the two caps:
 *
 *   `move_cap_deferred`            the weekly move cap — drops a store's team can handle in a week, sent or
 *                                  received; `cap` is moves a week
 *   `outbound_units_cap_deferred`  the weekly cap on pairs a store sends (bata-reqs ADR 9000); `cap` is pairs
 *                                  a week, `bound_at` the sending store
 *   `outbound_destinations_cap_deferred`  the weekly cap on the stores a store sends to (bata-platform ADR
 *                                  9058); `cap` is stores a week, `bound_at` the sending store
 *
 * one row per deferral (store × article × ship week, never a size: the cap counts moves), both stages — the
 * run's own distribute and the size-run repair execution of its tick. The planner's transfer filters are
 * NOT applied: a held-back move is not a transfer, it has no purpose, decision or net benefit to filter on.
 *
 * **Codes.** The CSV keeps every code as a code (`reason_code`, `stage`), like the other CSVs; the Excel
 * says them in words (`display`).
 *
 * **Not recorded is not «nothing held back».** A run older than the record, or a read that failed, gets
 * one row saying so (`HELD_BACK_ABSENT_COLUMNS`), never an empty sheet that would read as "the caps held
 * nothing back" — the store panel keeps the same two findings apart.
 */

import { callOperation, collectPages } from '@/api';
import { listMoveCapDeferrals, moveCapDeferralSummary } from '@/api/generated';
import type { MoveCapDeferral } from '@/api/generated';
import type { ExportCell, ExportColumn, WorkbookSheet } from '@/export';

import { REASON_MOVE_CAP, REASON_OUTBOUND_DESTINATIONS_CAP, REASON_OUTBOUND_UNITS_CAP, stageLabelKey } from './MoveCapDeferralsPanel';

/** The reasons a cap writes — the rows the sheet and the CSV list. */
export const HELD_BACK_REASONS: readonly string[] = [REASON_MOVE_CAP, REASON_OUTBOUND_UNITS_CAP, REASON_OUTBOUND_DESTINATIONS_CAP];

/** The id of the CSV a page offers beside the Why file (`../export-resolve-testing.ts`, `UNROUTED_EXPORTS`). */
export const HELD_BACK_EXPORT_ID = 'inseason.transferWhy.heldBack';
export const HELD_BACK_TITLE_KEY = 'inseason.heldBackMoves.title';
export const HELD_BACK_SHEET_TITLE_KEY = 'inseason.heldBackMoves.sheetTitle';

/** Why a file lists no held-back moves although some may exist. Each has its own sentence. */
export const HELD_BACK_ABSENCES = ['no_run', 'not_recorded', 'unread'] as const;
export type HeldBackAbsence = (typeof HELD_BACK_ABSENCES)[number];

/** What a file can say of a run's held-back moves: the rows (possibly none), or why there are none to say. */
export type HeldBackMoves =
  | { readonly state: 'read'; readonly runId: string; readonly rows: readonly MoveCapDeferral[] }
  | { readonly state: 'absent'; readonly runId: string | null; readonly reason: HeldBackAbsence };

const ABSENCE_KEY: Readonly<Record<HeldBackAbsence, string>> = {
  no_run: 'inseason.heldBackMoves.absent.noRun',
  not_recorded: 'inseason.heldBackMoves.absent.notRecorded',
  unread: 'inseason.heldBackMoves.absent.unread',
};

const REASON_KEY: Readonly<Record<string, string>> = {
  [REASON_MOVE_CAP]: 'inseason.moveCap.reason.capFull',
  [REASON_OUTBOUND_UNITS_CAP]: 'inseason.moveCap.reason.unitsCapFull',
  [REASON_OUTBOUND_DESTINATIONS_CAP]: 'inseason.moveCap.reason.destinationsCapFull',
};

/** The listed sheet's — and the CSV's — columns, in the order the engine's record reads. */
export const HELD_BACK_COLUMNS: readonly ExportColumn[] = [
  {
    key: 'reason_code', headerKey: 'inseason.moveCap.column.reason', kind: 'label', freeze: true,
    descriptionKey: 'inseason.columnGuide.heldBackMoves.reason_code',
    display: (code, t) => {
      const key = REASON_KEY[code];
      return key === undefined ? t('inseason.moveCap.reason.other', { code }) : t(key);
    },
  },
  {
    key: 'stage', headerKey: 'inseason.moveCap.column.stage', kind: 'label',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.stage',
    display: (code, t) => t(stageLabelKey(code)),
  },
  {
    key: 'store_key', headerKey: 'inseason.heldBackMoves.column.storeKey', kind: 'text',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.store_key',
  },
  {
    key: 'donor_store_key', headerKey: 'inseason.heldBackMoves.column.donorStoreKey', kind: 'text',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.donor_store_key',
  },
  {
    key: 'article_key', headerKey: 'inseason.column.article', kind: 'text',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.article_key',
  },
  {
    key: 'ship_week', headerKey: 'inseason.column.shipWeek', kind: 'date',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.ship_week',
  },
  {
    key: 'units', headerKey: 'inseason.moveCap.column.units', kind: 'integer', unit: 'pairs', total: 'sum',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.units',
  },
  {
    key: 'bound_at', headerKey: 'inseason.moveCap.column.boundAt', kind: 'text',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.bound_at',
  },
  {
    key: 'cap', headerKey: 'inseason.heldBackMoves.column.cap', kind: 'integer',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.cap',
  },
  {
    key: 'proposed_moves', headerKey: 'inseason.moveCap.column.proposedMoves', kind: 'integer',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.proposed_moves',
  },
  {
    key: 'deferral_id', headerKey: 'inseason.heldBackMoves.column.deferralId', kind: 'text', technical: true, identifies: true,
    descriptionKey: 'inseason.columnGuide.heldBackMoves.deferral_id',
  },
];

/**
 * The column when the file can list nothing: the sentence. The run it is about is the file's provenance
 * (`run_id` on every CSV row, «Tecnico» in the Excel), so it is not repeated here.
 */
export const HELD_BACK_ABSENT_COLUMNS: readonly ExportColumn[] = [
  {
    key: 'note', headerKey: 'inseason.heldBackMoves.column.note', kind: 'label',
    descriptionKey: 'inseason.columnGuide.heldBackMoves.note',
    display: (code, t) => t(ABSENCE_KEY[code as HeldBackAbsence] ?? code),
  },
];

/** An empty cell: no single other store (a distribute drop's donors are pooled), or no ship week (a consolidation). */
const BLANK: ExportCell = { kind: 'text', value: '' };

function heldBackRow(row: MoveCapDeferral): readonly ExportCell[] {
  return [
    { kind: 'label', value: row.reason_code, code: row.reason_code },
    { kind: 'label', value: row.stage, code: row.stage },
    { kind: 'text', value: row.store_key },
    row.donor_store_key === null ? BLANK : { kind: 'text', value: row.donor_store_key },
    { kind: 'text', value: row.article_key },
    row.ship_week === null ? BLANK : { kind: 'date', iso: row.ship_week },
    { kind: 'integer', raw: row.units },
    { kind: 'text', value: row.bound_at },
    { kind: 'integer', raw: row.cap },
    { kind: 'integer', raw: row.proposed_moves },
    { kind: 'text', value: row.deferral_id },
  ];
}

/** Held back by a cap, in a stable order: by stage, then store, article, ship week and the record's id. Pure. */
export function heldBackRows(rows: readonly MoveCapDeferral[]): readonly MoveCapDeferral[] {
  return rows
    .filter((row) => HELD_BACK_REASONS.includes(row.reason_code))
    .slice()
    .sort((a, b) => (
      a.stage.localeCompare(b.stage)
      || a.store_key.localeCompare(b.store_key)
      || a.article_key.localeCompare(b.article_key)
      || (a.ship_week ?? '').localeCompare(b.ship_week ?? '')
      || a.deferral_id.localeCompare(b.deferral_id)
    ));
}

/** The columns and rows for what a file can say of the run's held-back moves. Pure. */
export function heldBackTable(found: HeldBackMoves): {
  readonly columns: readonly ExportColumn[];
  readonly rows: readonly (readonly ExportCell[])[];
} {
  if (found.state === 'read') return { columns: HELD_BACK_COLUMNS, rows: heldBackRows(found.rows).map(heldBackRow) };
  return {
    columns: HELD_BACK_ABSENT_COLUMNS,
    rows: [[{ kind: 'label', value: found.reason, code: found.reason }]],
  };
}

/** The sheet of the Why workbook. Pure. */
export function heldBackSheet(found: HeldBackMoves): WorkbookSheet {
  const { columns, rows } = heldBackTable(found);
  return { titleKey: HELD_BACK_SHEET_TITLE_KEY, columns, rows: () => rows };
}

/**
 * The run's held-back moves: whether any stage recorded its deferrals (`…/move-cap-deferrals/summary`), then
 * every page of them. Never rejects — a failed read is `unread`; a run no stage recorded is `not_recorded`.
 */
export async function readHeldBackMoves(runId: string | null): Promise<HeldBackMoves> {
  if (runId === null) return { state: 'absent', runId: null, reason: 'no_run' };
  try {
    const summary = await callOperation(moveCapDeferralSummary, { path: { run_id: runId } });
    if (!summary.stages.some((stage) => stage.recorded)) return { state: 'absent', runId, reason: 'not_recorded' };
    const rows = await collectPages(listMoveCapDeferrals, { path: { run_id: runId } });
    return { state: 'read', runId, rows };
  } catch {
    return { state: 'absent', runId, reason: 'unread' };
  }
}
